import React, { useState, useEffect } from "react";
import { List } from "antd";
import { useTranslation } from "react-i18next";
import { OpModal } from "components/customAntd/DLS/OpModal/OpModal";
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { formatFullName, maskName } from "utils/utils";
import * as kioskApi from "components/auth/selfSignIn/services/kioskApi";

interface ScheduledVisitor {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  company?: string;
  email?: string;
  phone?: string;
  hostName?: string;
  visitorType?: string;
  scheduleStart: string;
  scheduleEnd: string;
  signedAgreements?: string[];
}

interface ScheduledVisitorSelectionPageProps {
  scheduledVisitors: ScheduledVisitor[];
  selectedVisitor: ScheduledVisitor | null;
  isNameNotOnList: boolean;
  onSelectVisitor: (visitor: ScheduledVisitor) => void;
  onNameNotOnList: () => void;
}

const ScheduledVisitorSelectionPage: React.FC<
  ScheduledVisitorSelectionPageProps
> = ({
  scheduledVisitors,
  selectedVisitor,
  isNameNotOnList,
  onSelectVisitor,
  onNameNotOnList,
}) => {
  const { t } = useTranslation();
  const [selectedVisitorId, setSelectedVisitorId] = useState<number | null>(
    selectedVisitor?.id || null,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalVisitor, setModalVisitor] = useState<ScheduledVisitor | null>(
    null,
  );

  // Update selected visitor when prop changes
  useEffect(() => {
    if (selectedVisitor) {
      setSelectedVisitorId(selectedVisitor.id);
    } else if (!isNameNotOnList) {
      setSelectedVisitorId(null);
    }
  }, [selectedVisitor, isNameNotOnList]);

  const handleVisitorClick = (visitor: ScheduledVisitor) => {
    // Show modal with visitor details
    setModalVisitor(visitor);
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    if (modalVisitor) {
      setSelectedVisitorId(modalVisitor.id);
      onSelectVisitor(modalVisitor);
    }
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setModalVisitor(null);
  };

  const handleNameNotOnListClick = () => {
    setSelectedVisitorId(null); // Clear visitor selection
    onNameNotOnList();
  };

  const formatScheduleTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDescription = (visitor: ScheduledVisitor) => {
    const parts = [];
    if (visitor.company) {
      parts.push(visitor.company);
    }
    if (visitor.scheduleStart) {
      parts.push(formatScheduleTime(visitor.scheduleStart));
    }
    return parts.join(" • ");
  };

  const renderModalContent = () => {
    if (!modalVisitor) return null;

    const items: any[] = [];

    // Name - always shown
    items.push({
      key: "name",
      label: t("selfSignIn.scheduledVisitor.labels.name"),
      children: formatFullName(
        modalVisitor.firstName,
        modalVisitor.middleName,
        modalVisitor.lastName,
      ),
    });

    // Company
    if (modalVisitor.company) {
      items.push({
        key: "company",
        label: t("selfSignIn.scheduledVisitor.labels.company"),
        children: modalVisitor.company,
      });
    }

    // Email
    if (modalVisitor.email) {
      items.push({
        key: "email",
        label: t("selfSignIn.scheduledVisitor.labels.email"),
        children: modalVisitor.email,
      });
    }

    // Phone
    if (modalVisitor.phone) {
      items.push({
        key: "phone",
        label: t("selfSignIn.scheduledVisitor.labels.phone"),
        children: modalVisitor.phone,
      });
    }

    // Host
    if (modalVisitor.hostName) {
      items.push({
        key: "host",
        label: t("selfSignIn.scheduledVisitor.labels.host"),
        children: modalVisitor.hostName,
      });
    }

    // Visitor Type
    if (modalVisitor.visitorType) {
      items.push({
        key: "visitorType",
        label: t("selfSignIn.scheduledVisitor.labels.visitorType"),
        children: modalVisitor.visitorType,
      });
    }

    // Schedule
    if (modalVisitor.scheduleStart) {
      const scheduleText = `${formatScheduleTime(modalVisitor.scheduleStart)}${
        modalVisitor.scheduleEnd
          ? ` - ${formatScheduleTime(modalVisitor.scheduleEnd)}`
          : ""
      }`;
      items.push({
        key: "schedule",
        label: t("selfSignIn.scheduledVisitor.labels.scheduledTime"),
        children: scheduleText,
      });
    }

    return (
      <List
        bordered
        dataSource={items}
        renderItem={(item: any) => (
          <List.Item style={{ display: "block", padding: "12px 16px" }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: "13px",
                color: "#666",
                marginBottom: "4px",
              }}
            >
              {item.label}
            </div>
            <div style={{ fontSize: "15px" }}>{item.children}</div>
          </List.Item>
        )}
      />
    );
  };

  return (
    <div className="workflow-container">
      <div className="selection-message">
        {t("selfSignIn.scheduledVisitor.selectPrompt")}
      </div>

      <OpModal
        title={t("selfSignIn.scheduledVisitor.modalTitle")}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t("selfSignIn.scheduledVisitor.selectButton")}
        cancelText={t("common.cancel")}
        width={600}
      >
        {renderModalContent()}
      </OpModal>

      <div className="visitor-list-container">
        <List
          bordered
          dataSource={scheduledVisitors}
          renderItem={(visitor) => (
            <List.Item
              key={visitor.id}
              className={`visitor-list-item ${selectedVisitorId === visitor.id ? "selected" : ""}`}
              onClick={() => handleVisitorClick(visitor)}
            >
              <List.Item.Meta
                title={formatFullName(
                  visitor.firstName,
                  visitor.middleName,
                  maskName(visitor.lastName),
                )}
                description={formatDescription(visitor)}
              />
            </List.Item>
          )}
        />
      </div>

      <div className="not-on-list-button">
        <OpButton
          type="default"
          size="large"
          onClick={handleNameNotOnListClick}
          style={
            isNameNotOnList
              ? {
                  backgroundColor: "#f0f9ff",
                  borderColor: "#0891b2",
                  color: "#0891b2",
                  borderWidth: "2px",
                }
              : {}
          }
        >
          {t("selfSignIn.scheduledVisitor.notOnList")}
        </OpButton>
      </div>
    </div>
  );
};

export default ScheduledVisitorSelectionPage;

// Handler function to be called by parent component
export const handleScheduledVisitorSelectionNext = async (
  selectedVisitor: any | null,
  isNameNotOnList: boolean,
  workflowByVisitorType: any[],
  firstName: string,
  lastName: string,
  token: string,
  workflowInConfig?: any,
  middleName?: string,
): Promise<{
  type: "redFlag" | "scheduledVisitor" | "walkIn";
  visitorTypes?: any[];
}> => {
  // Check if user made a selection
  if (!selectedVisitor && !isNameNotOnList) {
    throw new Error("selfSignIn.scheduledVisitor.noSelectionMade");
  }

  if (isNameNotOnList) {
    // Walk-in visitor flow
    // Filter visitor types that have workflowInConfig (not null)
    const availableVisitorTypes = workflowByVisitorType
      .filter((item: any) => item.workflowInConfig !== null)
      .map((item: any) => ({
        label: item.visitorType?.name || "",
        value: item.visitorType?.id?.toString() || "",
        id: item.visitorType?.id,
        languageCode: item.visitorType?.languageCode,
      }))
      .filter((type: any) => type.label && type.value);

    // Check against Red Flag API only if workflowByVisitorType is empty
    // If workflowByVisitorType is not empty, red flag check will be done after visitor type selection
    if (
      availableVisitorTypes.length === 0 &&
      workflowInConfig?.checkRedFlag === 1
    ) {
      const redFlagData = await kioskApi.checkRedFlag(
        firstName,
        lastName,
        token,
      );

      const isRedFlagged = kioskApi.isVisitorRedFlagged(
        firstName,
        lastName,
        redFlagData,
      );

      if (isRedFlagged) {
        return { type: "redFlag" };
      }
    }

    return {
      type: "walkIn",
      visitorTypes: availableVisitorTypes,
    };
  } else {
    // Scheduled visitor selected - check red flag
    // Find the visitor type config for the selected scheduled visitor
    let shouldCheckRedFlag = false;

    if (selectedVisitor) {
      const visitorTypeId = selectedVisitor.visitorTypeId;

      // Try to find visitor-type-specific config
      const visitorTypeConfig =
        visitorTypeId &&
        workflowByVisitorType &&
        workflowByVisitorType.length > 0
          ? workflowByVisitorType.find(
              (item: any) => item.visitorType?.id === visitorTypeId,
            )
          : null;

      if (visitorTypeConfig) {
        // Found visitor-type-specific config - use it
        if (visitorTypeConfig.workflowInConfig?.checkRedFlag === 1) {
          shouldCheckRedFlag = true;
        }
      } else {
        // No visitor-type-specific config found (or visitorType not in workflowByVisitorType) - use default
        if (workflowInConfig?.checkRedFlag === 1) {
          shouldCheckRedFlag = true;
        }
      }

      // Perform red flag check if needed
      if (shouldCheckRedFlag) {
        const redFlagData = await kioskApi.checkRedFlag(
          firstName,
          lastName,
          token,
        );

        const isRedFlagged = kioskApi.isVisitorRedFlagged(
          firstName,
          lastName,
          redFlagData,
        );

        if (isRedFlagged) {
          return { type: "redFlag" };
        }
      }
    }

    return { type: "scheduledVisitor" };
  }
};
